import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { CommonMethodService } from '../../common/commonMethod.service';
import { AccountService } from '../../services/account.service';
import { UserGroupService } from '../../services/userGroup.service';
import { UserService } from '../../services/user.service';
import { RegistrationCode } from '../../models/registrationCode.model';

@Component({
  selector: 'app-sigup',
  templateUrl: 'signup.component.html',
  providers: [AccountService, CommonMethodService, UserGroupService, UserService]
})
export class SignUpComponent implements OnInit, OnDestroy {

  signupForm: FormGroup;
  codeForm: FormGroup;
  subscribe: Array<Subscription> = [];
  loader: boolean = false;
  step: number = 1;
  registrationCode: RegistrationCode;
  submitted: boolean = false;
  flatList: any[] = [];
  assignedGroupName: string = '';
  countryList: any[] = [];

  constructor(private route: Router, private acticeRoute: ActivatedRoute,
    private toaster: ToastrService, private commonMethod: CommonMethodService, private accountService: AccountService,
    private userGroupService: UserGroupService, private userService: UserService) {}

  ngOnInit(): void {
    this.codeForm = new FormGroup({
      code: new FormControl('', [Validators.required]),
    });

    this.signupForm = new FormGroup({
      fullName: new FormControl('', [Validators.required, Validators.maxLength(50)]),
      email: new FormControl('', [Validators.email, Validators.maxLength(50)]),
      gender: new FormControl('', [Validators.required]),
      username: new FormControl('', [Validators.required, Validators.minLength(5), Validators.maxLength(20)]),
      password: new FormControl('', [Validators.required, Validators.minLength(5), Validators.maxLength(20)]),
      confirmPassword: new FormControl('', [Validators.required, Validators.minLength(5), Validators.maxLength(20)]),
      contactNo: new FormControl('', [Validators.required]),
      dialingCode: new FormControl('', [Validators.required]),
      address: new FormControl(''),
      country: new FormControl(''),
      county: new FormControl(''),
      city: new FormControl(''),
      premiseHierarchyDetailsId: new FormControl(''),
      employeeId: new FormControl(''),
      documentType: new FormControl(''),
      documentId: new FormControl(''),
      profile: new FormControl(''),
      nationality: new FormControl(''),
      secContactNo: new FormControl(''),
      secDialingCode: new FormControl(''),
      timeIn: new FormControl(''),
      timeOut: new FormControl(''),
      residentType: new FormControl(''),
      userGroupId: new FormControl(''),
    });
  }

  verifyCode(): void {
    if (this.codeForm.invalid) {
      this.commonMethod.checkFormValidOrNot(this.codeForm);
      return;
    }

    this.loader = true;
    const code = this.codeForm.value.code;
    this.subscribe.push(this.accountService.validateRegistrationCode(code).subscribe(
      (data: RegistrationCode) => {
        this.loader = false;
        this.registrationCode = data;
        this.step = 2;
        this.toaster.success('Code verified. Please fill in your details.');
        
        const premiseControl = this.signupForm.get('premiseHierarchyDetailsId');
        const residentTypeControl = this.signupForm.get('residentType');
        
        if (this.isDeviceRole()) {
          premiseControl.clearValidators();
          premiseControl.setValue('');
          premiseControl.disable();
          residentTypeControl.clearValidators();
          residentTypeControl.setValue('');
          residentTypeControl.disable();
        } else if (this.isResidentRole()) {
          premiseControl.setValidators([Validators.required]);
          premiseControl.enable();
          residentTypeControl.setValidators([Validators.required]);
          residentTypeControl.enable();
        } else {
          premiseControl.setValidators([Validators.required]);
          premiseControl.enable();
          residentTypeControl.clearValidators();
          residentTypeControl.setValue('');
          residentTypeControl.disable();
        }
        
        premiseControl.updateValueAndValidity();
        residentTypeControl.updateValueAndValidity();
        
        if (data.userGroupId) {
          this.signupForm.get('userGroupId').setValue(data.userGroupId);
          this.userGroupService.findUserGroupById(data.userGroupId).subscribe(
            (groupData: any) => {
              this.assignedGroupName = groupData.groupName || 'Group ' + data.userGroupId;
            },
            error => {
              this.assignedGroupName = 'Group ' + data.userGroupId;
            }
          );
        }
        
        if (data.accountId) {
          this.loadPremiseOptions(data.accountId);
          this.loadCountryList();
        }
      }, error => {
        this.loader = false;
        this.commonMethod.returnErrorMessage(error);
      }));
  }

  loadPremiseOptions(accountId: number): void {
    this.subscribe.push(this.accountService.getAllPremiseLevel(accountId).subscribe(
      (levels: any[]) => {
        if (!levels || levels.length === 0) {
          this.flatList = [];
          return;
        }
        
        let maxLevel = 0;
        let maxLevelId: number;
        levels.forEach(level => {
          if (level.level > maxLevel) {
            maxLevel = level.level;
            maxLevelId = level.id;
          }
        });
        
        if (maxLevelId) {
          this.subscribe.push(this.accountService.getUpperLevelList(accountId, maxLevelId).subscribe(
            (upperData: any[]) => {
              if (upperData && upperData.length > 0) {
                this.flatList = upperData;
              } else {
                this.accountService.getAllFlatList(accountId).subscribe(
                  (flatData: any[]) => {
                    this.flatList = flatData || [];
                  },
                  error => {
                    console.error('Failed to load flat list', error);
                    this.flatList = [];
                  }
                );
              }
            },
            error => {
              console.error('Failed to load upper level list', error);
              this.flatList = [];
            }
          ));
        }
      },
      error => {
        console.error('Failed to load premise levels', error);
        this.flatList = [];
      }
    ));
  }

  loadCountryList(): void {
    this.subscribe.push(this.userService.getCountryList('').subscribe(
      (data: any[]) => {
        this.countryList = data || [];
      },
      error => {
        console.error('Failed to load country list', error);
        this.countryList = [];
      }
    ));
  }

  onCountryChange(): void {
    const selectedCountry = this.countryList.find(c => c.countryName === this.signupForm.get('country').value);
    if (selectedCountry) {
      this.signupForm.get('dialingCode').setValue(selectedCountry.dialingCode);
      this.signupForm.get('nationality').setValue(selectedCountry.nationality);
    } else {
      this.signupForm.get('dialingCode').setValue('');
      this.signupForm.get('nationality').setValue('');
    }
  }

  onSubmit(): void {
    if (this.signupForm.invalid) {
      this.commonMethod.checkFormValidOrNot(this.signupForm);
      return;
    }

    const details = this.signupForm.value;
    if (details.fullName === null || details.fullName === undefined || details.fullName.trim() === '') {
      this.toaster.error('Please enter full name');
      return;
    }
    if (details.username === null || details.username === undefined || details.username.trim() === '') {
      this.toaster.error('Please enter username');
      return;
    }
    if (details.password !== details.confirmPassword) {
      this.toaster.error('Password and confirm password must be same');
      return;
    }
    if (details.email === null || details.email === undefined || details.email.trim() === '') {
      this.toaster.error('Please enter email');
      return;
    }
    if (details.contactNo === null || details.contactNo === undefined || details.contactNo.trim() === '') {
      this.toaster.error('Please enter contact number');
      return;
    }
    if (details.dialingCode === null || details.dialingCode === undefined || details.dialingCode.trim() === '') {
      this.toaster.error('Please enter dialing code');
      return;
    }

    this.loader = true;
    const payload = {
      code: this.registrationCode.code,
      ...details
    };

    this.subscribe.push(this.accountService.submitRegistrationRequest(payload).subscribe(
      data => {
        this.loader = false;
        this.submitted = true;
        this.step = 3;
      }, error => {
        this.loader = false;
        this.commonMethod.returnErrorMessage(error);
      }));
  }

  getRoleDisplayName(): string {
    if (!this.registrationCode) return '';
    switch (this.registrationCode.roleType.toUpperCase()) {
      case 'EMPLOYEE':
        return 'Employee Admin';
      case 'RESIDENT':
        return 'Resident Admin';
      case 'DEVICE':
        return 'Device Admin';
      default:
        return this.registrationCode.roleType;
    }
  }

  isEmployeeRole(): boolean {
    return this.registrationCode !== undefined && this.registrationCode.roleType.toUpperCase() === 'EMPLOYEE';
  }

  isResidentRole(): boolean {
    return this.registrationCode !== undefined && this.registrationCode.roleType.toUpperCase() === 'RESIDENT';
  }

  isDeviceRole(): boolean {
    return this.registrationCode !== undefined && this.registrationCode.roleType.toUpperCase() === 'DEVICE';
  }

  ngOnDestroy(): void {
    this.subscribe.forEach(destroyExistingValue => {
      destroyExistingValue.unsubscribe();
    });
  }
}
